import { useFormContext } from 'react-hook-form';

import {
  assignQuestionProperties,
  assignTopLevelScorecardProperties,
} from 'features/Connect/features/ConnectATSIntegration/utils';
import {
  ATSConnectHiringRequestCreateReferenceContactIds,
  ATSConnectHiringRequestRequestReferenceTestIds,
} from 'data-testids/ATS';
import { Link, SelectChangeEvent, Stack, useTheme } from '@mui/material';
import {
  useQueryGetReferenceContacts,
  useQueryGetReferenceTemplates,
} from 'features/Connect/features/ConnectATSIntegration/api/referenceQueries';
import {
  ReferenceContact,
  ReferenceContactAndTemplateFormProps,
} from 'features/Connect/features/ConnectATSIntegration/types/referenceTypes';
import { ContactTemplateSelect } from './ContactTemplateSelect';
import auth from 'utils/auth';

export const ReferenceContactAndTemplateForm: React.FC<ReferenceContactAndTemplateFormProps> = ({
  candidateId,
  newReferenceContactId,
  isWriteReferenceCheck,
  onAddClick,
}) => {
  const theme = useTheme();
  const { setValue } = useFormContext();

  const districtId = auth.getUser().district?.id;

  const {
    data: referenceTemplates,
    isLoading: isTemplatesLoading,
    isError: isTemplatesError,
  } = useQueryGetReferenceTemplates(districtId);
  const {
    data: referenceContacts,
    isLoading: isContactsLoading,
    isError: isContactsError,
  } = useQueryGetReferenceContacts(candidateId);

  const handleTemplateChange = (event: SelectChangeEvent, onChange: (id: number) => void) => {
    const selectedTemplateId = Number(event.target.value);
    const reference = referenceTemplates.find(
      (referenceTemplate) => referenceTemplate.id === selectedTemplateId
    );
    onChange(selectedTemplateId);

    if (isWriteReferenceCheck) {
      assignTopLevelScorecardProperties(reference?.questions, setValue);
      assignQuestionProperties(reference?.questions, setValue);
    }
  };

  const handleContactChange = (
    event: SelectChangeEvent,
    onChange: (contact: ReferenceContact) => void
  ) => {
    const selectedContactId = Number(event.target.value);
    const contact = referenceContacts.find((contact) => contact.id === selectedContactId);
    onChange(contact);
  };

  return (
    <Stack sx={{ pt: 1 }} spacing={2.75}>
      <ContactTemplateSelect
        dataTestId={ATSConnectHiringRequestRequestReferenceTestIds.REFERENCE_CONTACT_SELECT}
        formId={'recipients'}
        handleChange={handleContactChange}
        isGetError={isContactsError}
        isLoading={isContactsLoading}
        items={referenceContacts}
        labelText="Reference Contacts"
        placeholderText="Select Contact"
        defaultValue={newReferenceContactId}
      />
      {!newReferenceContactId && (
        <Link
          data-testid={ATSConnectHiringRequestCreateReferenceContactIds.ADD_NEW_CONTACT_LINK}
          onClick={onAddClick}
          sx={{
            color: theme.palette.primary.dark,
            cursor: 'pointer',
            textDecoration: 'none',
          }}
        >
          + Add New Reference Contact
        </Link>
      )}
      <ContactTemplateSelect
        dataTestId={ATSConnectHiringRequestRequestReferenceTestIds.REFERENCE_TEMPLATE_SELECT}
        formId="reference_template_id"
        handleChange={handleTemplateChange}
        isGetError={isTemplatesError}
        isLoading={isTemplatesLoading}
        items={referenceTemplates}
        labelText="Reference Check Form"
        placeholderText="Select Reference Check Form"
      />
    </Stack>
  );
};
